import React, { useState } from "react";

import { faComments, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";

import {
	Icon,
	InputText,
	Popover,
	PopoverAnchor,
	PopoverContent,
	PopoverTrigger,
	Spinner,
} from "@performyard/py-components";
import { ASK_CONVERSATION_LIST_PAGE_SIZE } from "@performyard/service-contracts";

import { useAskConversations } from "../../../../api/hooks";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";

/**
 * Recent Conversations dropdown, hung off the panel header title (PY-13559).
 *
 * A Popover rather than the shared `DropDownMenu`: that component takes a flat
 * `options` array and has no room for a section label, a search field, or an
 * empty state, and its Radix `DropdownMenu` typeahead would swallow keystrokes
 * meant for the search box.
 */

type AskPyConversationMenuProps = {
	/** Header label. The active conversation's title, or the product name before the first turn. */
	title: string;
	/**
	 * The conversation on screen. Excluded from the list, per the design — the
	 * header already names it, and selecting it would spend a fetch to redraw the
	 * thread the user is looking at.
	 */
	activeConversationId: string | null;
	onSelect: (conversationId: string) => void;
};

/**
 * Fetched deeper than the "Many" frame's five rows because the search frame
 * shows a longer, scrolling list. Crossing five is what reveals the search bar
 * (the AC's "exceeds the visible list size") and turns the list into a scroll
 * area — below that it renders whole, exactly as the None/One/Many frames do.
 */
const FETCH_LIMIT = ASK_CONVERSATION_LIST_PAGE_SIZE * 2;

/**
 * Long enough that an ordinary typing burst is one request rather than one per
 * character — each is a list query running a case-insensitive regex — and short
 * enough that the results still feel attached to the keystroke.
 */
const SEARCH_DEBOUNCE_MS = 300;

export const AskPyConversationMenu: React.FC<AskPyConversationMenuProps> = ({
	title,
	activeConversationId,
	onSelect,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState("");

	// `search` drives the input; the debounced copy drives the query, so typing
	// does not fire a request per character.
	const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

	// Only fetched while open — the panel header renders on every page.
	const { data, isLoading } = useAskConversations({
		limit: FETCH_LIMIT,
		search: debouncedSearch,
		enabled: isOpen,
	});

	// Filtered here rather than in the query so the fetch stays cacheable across
	// conversation switches — the excluded row changes far more often than the
	// underlying list does.
	const conversations = (data?.conversations ?? []).filter(
		(conversation) => conversation.id !== activeConversationId
	);

	// Stays visible once searching, even when the results are few or none —
	// otherwise typing a query that matches nothing removes the field that
	// produced it, and the user cannot get back.
	const showSearch = search.length > 0 || conversations.length > ASK_CONVERSATION_LIST_PAGE_SIZE;

	const handleSelect = (conversationId: string): void => {
		setIsOpen(false);
		setSearch("");
		onSelect(conversationId);
	};

	return (
		<Popover
			open={isOpen}
			onOpenChange={(open) => {
				setIsOpen(open);
				if (!open) setSearch("");
			}}
		>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="flex min-w-0 cursor-pointer appearance-none items-center gap-2 border-none bg-transparent p-0 text-left"
					data-testid="ask-py-conversation-menu-trigger"
					aria-label="Recent conversations"
					aria-haspopup="dialog"
					aria-expanded={isOpen}
				>
					<img src="/static/img/py-logo/py-round-icon.svg" alt="" className="size-6 shrink-0" />
					{/* Anchor (not the trigger) so the panel lines up with the title, not the logo.
					    The whole button still opens — logo stays clickable; hover stays pill-only.
					    max-w-72 matches the menu's w-72: leftover in the 420px dock after header
					    padding, action buttons, and the logo+gap. */}
					<PopoverAnchor asChild>
						<span
							className="flex max-w-72 min-w-0 items-center gap-2 rounded-1 px-1 py-0.5 hover:bg-neutral-1 active:bg-neutral-2"
							data-testid="ask-py-conversation-menu-title"
						>
							<span className="truncate text-base leading-6 text-neutral-9">{title}</span>
							<Icon icon={faChevronDown} size={16} color="neutral-5" />
						</span>
					</PopoverAnchor>
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" sideOffset={4} className="w-72 p-0" data-testid="ask-py-conversation-menu">
				<div className="flex flex-col py-2">
					<span className="px-3 py-1 text-paragraph-3 text-neutral-5">Recent Conversations</span>

					{showSearch && (
						<div className="px-3 py-1">
							<InputText
								value={search}
								onChange={setSearch}
								placeholder="Search..."
								aria-label="Search conversations"
								leftIcon={faSearch}
								clearable
								data-testid="ask-py-conversation-search"
							/>
						</div>
					)}

					{isLoading && !data ? (
						<div className="flex items-center justify-center py-6">
							<Spinner />
						</div>
					) : conversations.length === 0 ? (
						// Two different nothings: an account with no history at all, and a
						// search that matched none of it. Saying "No Recent Conversations"
						// to someone who just typed a query reads as data loss.
						<div
							className="flex flex-col items-center gap-2 px-3 py-6"
							data-testid="ask-py-conversation-menu-empty"
						>
							<Icon icon={faComments} size={20} color="neutral-5" />
							<span className="text-paragraph-3 text-neutral-5">
								{search ? "No matching conversations" : "No Recent Conversations"}
							</span>
						</div>
					) : (
						// Caps at roughly eight rows and scrolls past that, matching the
						// search frame. The popover must not grow with the list — it is
						// docked inside a 420px panel with the thread behind it.
						<ul className="m-0 flex max-h-64 list-none flex-col overflow-y-auto p-0">
							{conversations.map((conversation) => (
								<li key={conversation.id}>
									<button
										type="button"
										onClick={() => handleSelect(conversation.id)}
										className="flex w-full cursor-pointer appearance-none border-none bg-transparent px-3 py-2 text-left text-paragraph-3 text-neutral-9 hover:bg-neutral-1"
										data-testid={`ask-py-conversation-option-${conversation.id}`}
									>
										<span className="min-w-0 flex-1 truncate">{conversation.title}</span>
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default AskPyConversationMenu;
